import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {ACTIVE_USER, IMPERSONATING_USER,} from '@modules/auth/providers/auth.provider';
import {BehaviorSubject, catchError, EMPTY, map, Observable, switchMap, take, tap,} from 'rxjs';
import {User} from '@modules/user/models/user.model';
import {AuthService} from '@modules/auth/services/auth.service';
import {APP_SIDEBAR, SidebarConfig, SideBarItem,} from '@config/sidebar.config';
import {UserType} from '@generated/graphql';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {APP_ROUTES, AppRoutes} from '@config/app-routes.config';
import {Title} from '@angular/platform-browser';
import {PaymentProviderService} from '@modules/payment/services/payment-provider.service';
import {BusinessPlanService} from '@modules/account-settings/pages/business-profile/services/business-plan.service';
import {cloneDeep} from 'lodash-es';
import {WireTransferBankService} from '@modules/wire-transfer-bank/services/wire-transfer-bank.service';
import {PusherService} from "@shared/services/pusher.service";
import {
  BusinessProfileService
} from "@modules/account-settings/pages/business-profile/services/business-profile.service";
import {DateTime} from "luxon";

@UntilDestroy()
@Component({
  selector: 'main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  currentYear = DateTime.now().year;
  favIcon: HTMLLinkElement = document.querySelector('[rel="icon"]');

  paymentProviders$ = this.paymentProviderService.activeEntities;
  banks$ = this.wireTransferBankService.entities$;
  businessPlan$ = this.businessPlanService.businessPlan$;

  private readonly _sidebar$ = new BehaviorSubject<SidebarConfig>(cloneDeep(this.defaultSidebar));

  sidebar$  = this._sidebar$.asObservable()

  menuSections$ = this.sidebar$.pipe(
    map(s => this.filterHidden(s.menuSection))
  )

  constructor(
    @Inject(APP_ROUTES) public readonly appRoutes: AppRoutes,
    @Inject(ACTIVE_USER) public readonly activeUser$: Observable<User>,
    @Inject(IMPERSONATING_USER) private readonly impersonatingUser$: Observable<UserType>,
    @Inject(APP_SIDEBAR) private readonly defaultSidebar: SidebarConfig,
    private readonly wireTransferBankService: WireTransferBankService,
    private readonly authService: AuthService,
    private readonly pusherService: PusherService,
    private readonly paymentProviderService: PaymentProviderService,
    private readonly titleService: Title,
    private readonly businessProfileService: BusinessProfileService,
    private readonly businessPlanService: BusinessPlanService,
  ) {
  }

  ngOnInit(): void {
    // Setup Pusher
    this.pusherService.setup()

    // Setup Pusher & Sidebar
    this.activeUser$.pipe(
      switchMap(user => this.businessPlanService.visiblePayment$().pipe(
        tap(visiblePayment => {
          const sidebar = cloneDeep(this._sidebar$.value);
          sidebar.updateByUser(user, visiblePayment);
          this._sidebar$.next(sidebar);
        })
      )),
      untilDestroyed(this)
    ).subscribe();

    // Update title & favicon
    this.businessProfileService.activeEntity$.pipe(
      tap(entity => {
        const headerTitle = entity.headerTitle || 'Becorps'
        this.titleService.setTitle(headerTitle);
        this.favIcon.href = entity.faviconUrl || 'assets/images/logo/becorps-white-logo-only.svg';
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.pusherService.disconnect();
  }

  sidebarHandler(isOpenSidebar: boolean): void {
    const sidebar = this._sidebar$.value

    sidebar.isOpenSidebar = !isOpenSidebar

    this._sidebar$.next(cloneDeep(sidebar));
  }

  filterHidden(items: SideBarItem[] = []): SideBarItem[] {
    return items.filter(item => !item.isHidden);
  }
}
